// material-ui
import { useEffect, useState, forwardRef } from 'react';
import { useTheme, styled } from '@mui/material/styles';
import {
    Grid,
    Link,
    Typography,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    CircularProgress,
    Alert,
    Divider,
    Stack
} from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import SchoolIcon from '@mui/icons-material/School';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import Tooltip from '@mui/material/Tooltip';

// project imports
import { gridSpacing } from 'store/constant';
import LogoFooter from 'ui-component/LogoFooter';
import axios from 'utils/Axios';
import { useDispatch, useSelector } from 'react-redux';
import { loadInfo, selectInteractionsInfo } from 'store/interactionsSlice';

// assets
import { AcademyIcon } from 'ui-component/Icons';

// styles
const FooterLink = styled(Link)(({ theme }) => ({
    color: `${theme.palette.text.primary} !important`,
    display: 'inline-flex',
    alignItems: 'center',
    textDecoration: 'none !important',
    '& svg': {
        fontsize: '1.125rem',
        marginRight: 8
    },
    '&:hover': {
        opacity: 0.8
    },
    userSelect: 'none'
}));

const FooterCard = styled(Card)(({ theme }) => ({
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    border: '1px solid',
    borderBottom: 0,
    borderColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.primary[200] + 75,
    [theme.breakpoints.down('md')]: {
        textAlign: 'center'
    },
    userSelect: 'none',
    '& *': {
        userSelect: 'none'
    }
}));

const FooterSubCard = styled(Card)(({ theme }) => ({
    padding: 0,
    margin: 0,
    borderRadius: 0,
    border: '1px solid',
    borderTop: 0,
    borderBottom: 0,
    borderColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.primary[200] + 75,
    background:  theme.palette.mode === 'dark' ? '#1a223f' : '#f7f7f7',
    [theme.breakpoints.down('md')]: {
        textAlign: 'center'
    },
    userSelect: 'none',
    '& *': {
        userSelect: 'none'
    }
}));

const VersionLink = styled('button')(({ theme }) => {
    const isDark = theme.palette.mode === 'dark';
    const hoverBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(25,118,210,0.08)';

    return {
        backgroundColor: 'transparent',
        border: 0,
        padding: '0.1rem 0.35rem',
        margin: '0 -0.35rem',
        font: 'inherit',
        fontWeight: 600,
        color: isDark ? theme.palette.grey[50] : theme.palette.text.primary,
        opacity: 0.85,
        cursor: 'pointer',
        borderRadius: 6,
        transition: 'color 0.2s ease, background-color 0.2s ease, opacity 0.2s ease',
        textDecoration: 'none',
        '&:hover': {
            color: isDark ? theme.palette.primary.light : theme.palette.primary.main,
            backgroundColor: hoverBg,
            opacity: 1,
            textDecoration: 'underline'
        },
        '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
            opacity: 1
        },
        '&:disabled': {
            cursor: 'not-allowed',
            color: theme.palette.text.disabled,
            backgroundColor: 'transparent',
            opacity: 0.6,
            textDecoration: 'none'
        }
    };
});

const ReleaseDialogContent = styled('div')(({ theme }) => ({
    margin: '0 20px 20px',
    padding: '20px 0',
    backgroundColor: theme.palette.background.paper
}));

const ReleaseDivider = styled(Divider)(({ theme }) => ({
    margin: '8px 0 16px',
    borderImage: `linear-gradient(to left, ${theme.palette.mode === 'dark' ? '#212946' : '#fff'} 1%, ${
        theme.palette.mode === 'dark' ? '#a5b0d6' : '#eeeeee'
    } 50%, ${theme.palette.mode === 'dark' ? '#212946' : '#fff'} 100%) 100% 0 100% 0/3px 0 3px 0 stretch`
}));

const getStoredDbVersion = () => {
    if (typeof window === 'undefined') {
        return '';
    }

    try {
        const stored = window.localStorage.getItem('db-version');
        return stored ? stored.trim() : '';
    } catch (error) {
        return '';
    }
};

const parseDbVersionParts = (version) => {
    if (!version || version.length < 6) {
        return null;
    }

    const baseYear = parseInt(version.slice(0, 2), 10);
    const month = parseInt(version.slice(2, 4), 10);
    const day = parseInt(version.slice(4, 6), 10);

    if ([baseYear, month, day].some((value) => Number.isNaN(value))) {
        return null;
    }

    const year = 2000 + baseYear;
    let hours = null;
    let minutes = null;

    if (version.length >= 10) {
        const rawHours = parseInt(version.slice(6, 8), 10);
        const rawMinutes = parseInt(version.slice(8, 10), 10);

        if (!Number.isNaN(rawHours) && !Number.isNaN(rawMinutes)) {
            hours = String(rawHours).padStart(2, '0');
            minutes = String(rawMinutes).padStart(2, '0');
        }
    }

    const pad = (value) => String(value).padStart(2, '0');

    return {
        day: pad(day),
        month: pad(month),
        year,
        hours,
        minutes
    };
};

const formatDbVersionLabel = (version) => {
    const parts = parseDbVersionParts(version);
    if (!parts) {
        return '';
    }

    const base = `${parts.day}.${parts.month}.${parts.year}`;
    return parts.hours && parts.minutes ? `Databáze: ${base} ${parts.hours}:${parts.minutes}` : `Databáze: ${base}`;
};

const formatDbVersionValue = (version) => {
    const parts = parseDbVersionParts(version);
    if (!parts) {
        return '';
    }

    const base = `${parts.day}.${parts.month}.${parts.year}`;
    return parts.hours && parts.minutes ? `${base} ${parts.hours}:${parts.minutes}` : base;
};

const ReleaseCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'highlight'
})(({ theme, highlight }) => {
    return {
        borderRadius: 14,
        padding: '20px 24px',
        border: `1px solid ${
            highlight
                ? 'rgba(59,130,246,0.35)'
                : theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.08)'
                : theme.palette.divider
        }`,
        backgroundColor: highlight
            ? 'rgba(59,130,246,0.05)'
            : theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.02)'
            : '#fff',
        boxShadow: highlight ? '0 0 6px rgba(59,130,246,0.15)' : 'none',
        position: 'relative',
        overflow: 'hidden',
        '& .release-label': {
            color: highlight
                ? '#60a5fa'
                : theme.palette.mode === 'dark'
                ? theme.palette.text.secondary
                : theme.palette.text.secondary
        }
    };
});

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// ==============================|| LANDING - FOOTER PAGE ||============================== //

const FooterPage = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const info = useSelector(selectInteractionsInfo);
    const {
        dbVersion: infoDbVersion = '',
        appVersion: infoAppVersion = '',
        status: infoStatus = 'idle'
    } = info || {};

    const fallbackAppVersion = process.env.REACT_APP_VERSION ? `v${process.env.REACT_APP_VERSION}` : 'v3.1.260111-80c9f12';
    const appVersion = fallbackAppVersion;
    const apiVersion = infoAppVersion || '';
    const dbVersion = infoDbVersion || getStoredDbVersion();
    const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
    const [releaseTarget, setReleaseTarget] = useState({ version: '', label: '' });
    const [releaseNotes, setReleaseNotes] = useState([]);
    const [releaseLoading, setReleaseLoading] = useState(false);
    const [releaseError, setReleaseError] = useState('');
    const releaseVersion = releaseTarget.version;

    useEffect(() => {
        if (infoStatus === 'idle') {
            dispatch(loadInfo());
        }
    }, [dispatch, infoStatus]);

    useEffect(() => {
        if (!dbVersion || typeof window === 'undefined') {
            return;
        }

        const existing = getStoredDbVersion();
        if (existing !== dbVersion) {
            window.localStorage.setItem('db-version', dbVersion);
        }
    }, [dbVersion]);

    useEffect(() => {
        if (!releaseDialogOpen || !releaseVersion) {
            return;
        }

        let isActive = true;
        setReleaseLoading(true);
        setReleaseError('');

        axios
            .get('/info/releases', {
                params: {
                    minVersion: releaseVersion
                }
            })
            .then(({ data }) => {
                if (!isActive) {
                    return;
                }

                setReleaseNotes(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (!isActive) {
                    return;
                }

                setReleaseError('Nepodařilo se načíst poznámky k verzi. Zkuste to prosím znovu.');
                setReleaseNotes([]);
            })
            .finally(() => {
                if (isActive) {
                    setReleaseLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [releaseDialogOpen, releaseVersion]);

    const getYear = () => {
        const currentYear = new Date().getFullYear();
        return currentYear;
    };

    const handleVersionClick = (type) => {
        const isApi = type === 'api';
        const version = isApi ? apiVersion : appVersion;

        if (!version) {
            return;
        }

        setReleaseNotes([]);
        setReleaseError('');
        setReleaseTarget({
            version,
            label: isApi ? 'API' : 'Aplikace'
        });
        setReleaseDialogOpen(true);
    };

    const handleCloseReleaseDialog = () => {
        setReleaseDialogOpen(false);
    };

    const formattedDbVersion = formatDbVersionLabel(dbVersion);
    const dbVersionDate = formatDbVersionValue(dbVersion);

    const versionMeta = [
        {
            key: 'app',
            label: 'Aplikace',
            value: appVersion
        },
        {
            key: 'api',
            label: 'API',
            value: apiVersion || ''
        },
        {
            key: 'db',
            label: 'Databáze',
            value: dbVersionDate || ''
        }
    ];

    const formattedSegments = [
        {
            key: 'app',
            content: (
                <span>
                    Aplikace:{' '}
                    <VersionLink type="button" onClick={() => handleVersionClick('app')}>
                        {appVersion}
                    </VersionLink>
                </span>
            )
        },
        apiVersion && {
            key: 'api',
            content: (
                <span>
                    API:{' '}
                    <VersionLink type="button" onClick={() => handleVersionClick('api')}>
                        {apiVersion}
                    </VersionLink>
                </span>
            )
        },
        formattedDbVersion && {
            key: 'db',
            content: formattedDbVersion
        }
    ].filter(Boolean);

    const dialogTitle = 'Release notes';

    const resolveVersionTags = (version) => {
        if (!version) return [];

        const matchesApi = apiVersion && version === apiVersion;
        const matchesApp = appVersion && version === appVersion;

        if (matchesApi && matchesApp) {
            return ['Aktuální verze'];
        }

        const tags = [];
        if (matchesApi) {
            tags.push('Aktuální verze API');
        }
        if (matchesApp) {
            tags.push('Aktuální verze aplikace');
        }

        return tags;
    };

    const renderDialogBody = () => {
        if (releaseLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                </Box>
            );
        }

        if (releaseError) {
            return (
                <Alert
                    severity="error"
                    variant="outlined"
                    sx={{
                        backgroundColor:
                            theme.palette.mode === 'dark'
                                ? 'rgba(244, 67, 54, 0.08)'
                                : 'rgba(244, 67, 54, 0.05)',
                        borderColor:
                            theme.palette.mode === 'dark' ? 'rgba(244,67,54,0.4)' : 'rgba(244,67,54,0.3)',
                        color: theme.palette.mode === 'dark' ? '#ffb4a9' : theme.palette.error.dark,
                        borderRadius: 2,
                        boxShadow: theme.palette.mode === 'dark' ? '0 0 10px rgba(244,67,54,0.15)' : 'none'
                    }}
                >
                    {releaseError}
                </Alert>
            );
        }

        if (releaseNotes.length === 0) {
            return (
                <Typography variant="body2" color="textSecondary">
                    Pro tuto verzi zatím nejsou k dispozici žádné poznámky.
                </Typography>
            );
        }

        return (
            <Stack spacing={3}>
                {releaseNotes.map((note, index) => {
                    const versionLabel = note.version ?? note.Version ?? `Verze ${index + 1}`;
                    const noteHtml = note.notes ?? note.Notes ?? '';
                    const tags = resolveVersionTags(versionLabel);
                    const isHighlighted = tags.length > 0;

                    return (
                        <ReleaseCard key={`${versionLabel}-${index}`} highlight={isHighlighted}>
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                                <Typography
                                    variant="h4"
                                    fontWeight={700}
                                    sx={{
                                        color: isHighlighted
                                            ? theme.palette.mode === 'dark'
                                                ? theme.palette.primary[200]
                                                : theme.palette.primary.main
                                            : theme.palette.text.primary,
                                        letterSpacing: 0.6
                                    }}
                                >
                                    {versionLabel}
                                </Typography>
                                {tags.length > 0 && (
                                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                        {tags.map((tag) => (
                                            <Typography
                                                key={tag}
                                                variant="caption"
                                                className="release-label"
                                                sx={{
                                                    textTransform: 'uppercase',
                                                    letterSpacing: 1,
                                                    fontWeight: 600,
                                                    border: '1px solid rgba(59,130,246,0.35)',
                                                    borderRadius: 999,
                                                    px: 1.5,
                                                    py: 0.3
                                                }}
                                            >
                                                {tag}
                                            </Typography>
                                        ))}
                                    </Stack>
                                )}
                            </Stack>
                            <Box
                                sx={{
                                    typography: 'body2',
                                    '& ul': { pl: 3 },
                                    '& p': { mb: 1.5 },
                                    '& a': { color: theme.palette.primary.main }
                                }}
                                dangerouslySetInnerHTML={{ __html: noteHtml }}
                            />
                        </ReleaseCard>
                    );
                })}
            </Stack>
        );
    };

    return (
        <>
            <FooterCard>
                <CardContent>
                    <Grid container alignItems="center" spacing={gridSpacing}>
                        <Grid item xs={12} sm={12} md={4}>
                            <Link href="https://www.drugagency.cz" target="_blank" underline="hover"><LogoFooter /></Link>
                        </Grid>
                        <Grid item xs={12} sm={12} md={8}>
                            <Grid
                                container
                                alignItems="center"
                                spacing={2}
                                sx={{ justifyContent: 'flex-end', [theme.breakpoints.down('md')]: { justifyContent: 'center' } }}
                            >
                                <Grid item>
                                    <FooterLink href="https://www.interakcniakademie.cz" target="_blank" underline="hover">
                                        <AcademyIcon />
                                        Interakční akademie
                                    </FooterLink>
                                </Grid>
                                <Grid item>
                                    <FooterLink href="https://www.lekovaakademie.cz" target="_blank" underline="hover">
                                        <SchoolIcon />
                                        Léková akademie
                                    </FooterLink>
                                </Grid>

                            </Grid>
                        </Grid>
                    </Grid>
                </CardContent>
            </FooterCard>
            <FooterSubCard>
                <Grid container alignItems="center" spacing={gridSpacing}>
                    <Grid item xs={12} sm={12} md={4}>
                        <Typography sx={{ color: theme.palette.text.primary, padding: '0.75rem 1.5rem 0.75rem 1.5rem' }} variant="subtitle2" component="div" >
                            DrugAgency a.s. © {getYear()} | Všechna práva vyhrazena
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12} md={8}>
                        <Grid
                            container
                            alignItems="center"
                            spacing={2}
                            sx={{ justifyContent: 'flex-end', [theme.breakpoints.down('md')]: { justifyContent: 'center' } }}
                        >
                            <Grid item>
                                <Typography
                                    sx={{ color: theme.palette.text.primary, padding: '0.75rem 1.5rem 0.75rem 1.5rem' }}
                                    variant="subtitle2"
                                    component="div"
                                >
                                    {formattedSegments.map((segment, index) => (
                                        <span key={segment.key} style={{ whiteSpace: 'nowrap' }}>
                                            {index > 0 && <span style={{ padding: '0 0.5rem' }}>{'\u00A0|\u00A0'}</span>}
                                            {segment.content}
                                        </span>
                                    ))}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </FooterSubCard>
            <Dialog
                open={releaseDialogOpen}
                onClose={handleCloseReleaseDialog}
                fullWidth
                maxWidth="md"
                keepMounted
                scroll="body"
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}>
                    {dialogTitle}
                    <Stack direction="row" spacing={0}>
                        <Tooltip title="Zavřít">
                            <span>
                                <IconButton onClick={handleCloseReleaseDialog}>
                                    <CloseIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <ReleaseDialogContent>
                        {!releaseTarget.version && (
                            <Stack spacing={0.5}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Poznámky k verzi
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Pro zobrazení změn klikněte na verzi v patičce.
                                </Typography>
                            </Stack>
                        )}
                        {versionMeta.some((item) => item.value) && (
                            <Stack
                                direction="row"
                                justifyContent="center"
                                alignItems="center"
                                spacing={2}
                                flexWrap="wrap"
                                sx={{
                                    mt: !releaseTarget.version ? 2 : 0,
                                    mb: 2
                                }}
                            >
                                {versionMeta.map((item) => (
                                    <MetaBox key={item.key} spacing={0.5}>
                                        <Typography color={"#fff"} className="meta-label" variant="caption" sx={{ textTransform: 'uppercase' }}>
                                            {item.label}
                                        </Typography>
                                        <Typography
                                            className="meta-value"
                                            variant="subtitle1"
                                            fontWeight={600}
                                            sx={{ color: item.value ? undefined : theme.palette.text.disabled }}
                                        >
                                            {item.value || '—'}
                                        </Typography>
                                    </MetaBox>
                                ))}
                            </Stack>
                        )}
                        <ReleaseDivider />
                        {renderDialogBody()}
                    </ReleaseDialogContent>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default FooterPage;
const MetaBox = styled(Stack)(({ theme }) => {
    const accent = '#9ad436';
    const isDark = theme.palette.mode === 'dark';
    return {
        flex: '0 0 auto',
        minWidth: '140px',
        borderRadius: 10,
        border: `1px solid ${accent}44`,
        padding: '8px 20px',
        textAlign: 'center',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isDark ? 'rgba(154,212,54,0.08)' : '#f7ffea',
        boxShadow: isDark ? '0 0 12px rgba(154,212,54,0.15)' : '0 1px 6px rgba(154,212,54,0.2)',
        '& .meta-label': {
            color: isDark ? '#cdd3f4' : '#000'
        },
        '& .meta-value': {
            color: isDark ? accent : '#6ead00ff'
        }
    };
});
